type MThensHandler<
  Maybes extends readonly (unknown | null | undefined)[] | [],
  Result,
> = (nonMaybes: {
  -readonly [K in keyof Maybes]: NonNullable<Maybes[K]>
}) => Result | null | undefined

export const mthens = <Maybes extends readonly (unknown | null | undefined)[] | [], Result>(
  maybes: Maybes,
  f: MThensHandler<Maybes, Result>,
): Result | null | undefined =>
  maybes.some(m => m === null || m === undefined)
    ? null
    : f(maybes as {-readonly [K in keyof Maybes]: NonNullable<K>})

export function maybe<I, O>(defaultValue: () => O, f: (v: I) => O, v?: I | null): O {
  return v === undefined || v === null ? defaultValue() : f(v)
}

export const fromMaybe = <T>(defaultValue: () => T, t?: T | null): T => t ?? defaultValue()

export const undefinedAsNull = <A>(x: A | undefined): A | null => fromMaybe(() => null, x)

export const nullAsUndefined = <A>(x: A | null): A | undefined => fromMaybe(() => undefined, x)

export const catMaybes = <T>(
  array: Array<NonNullable<T> | undefined | null>,
): Array<NonNullable<T>> => array.flatMap(x => (x === null || x === undefined ? [] : [x]))

export const mapMaybes = <A, B>(
  array: Array<A>,
  callback: (value: A, index: number, array: ReadonlyArray<A>) => NonNullable<B> | undefined | null,
): Array<NonNullable<B>> => catMaybes(array.map(callback))

// fmap for `null | undefined`
export const mmap = <I, O>(
  f: (v: NonNullable<I>) => O,
  v?: NonNullable<I> | null,
): O | undefined | null => (v === undefined ? undefined : v === null ? null : f(v))

// bind for `null | undefined`
// Note that `bind` and `map` have the same body in JavaScript
export const mthen = <I, O>(
  v: NonNullable<I> | undefined | null,
  f: (v: NonNullable<I>) => O | undefined | null,
): O | undefined | null => (v === undefined ? undefined : v === null ? null : f(v))

// mthen for effects (and not transformations, hence nothing is returned)
export function mEffect<V>(v: V | undefined | null, effect: (v: V) => void) {
  if (v !== null && v !== undefined) {
    effect(v)
  }
}

// create an object with the given property/value, when the value is present
export const mObj = <P extends string, V>(p: P, v?: V | null): Partial<Record<P, V>> =>
  v === null || v === undefined ? {} : ({[p]: v} as Record<P, V>)

/* asHTMLAttributeValue is used as a way to make an HTML attribute value
 * exist in the DOM or not. React does not add in the DOM HTML attributes
 * with an "undefined" value
 * Flow does not accept the empty object for spreading.
 * eg.
 *
 * const disabled = null
 * const disabledObj = {disabled: asHTMLAttributeValue(disabled)}
 * <button {...disabledObj} />
 */

export const asHTMLAttributeValue = <T>(value?: T | null): T | undefined => nullAsUndefined(value)
